import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import ApiService from "../../services/ApiService";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const Password = () => {
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const handleChangePassword = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
  
    const currentPassword = formData.get("current_password");
    const newPassword = formData.get("new_password");
    const confirmPassword = formData.get("confirm_password");
  
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match!");
      return;
    }
  
    try {
      setLoading(true);
  
      const response = await ApiService.request({
        method: "POST",
        url: `/company/changePassword`,
        data: {
          current_password: currentPassword,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
      });
  const data = response.data;

      if (data?.status === true) {
        toast.success(data?.message || "Password changed successfully!");
        form.reset();
      } else {
        toast.error(data?.message || "Something went wrong, please try again!");
      }
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || "Server error, please try again!");
    } finally {
      setLoading(false);
    }
  };
  

  return (
   <form
      onSubmit={handleChangePassword}
      className="p-4"
      style={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        maxWidth: "500px",
        margin: "auto",
        direction: isRTL ? "rtl" : "ltr", // Added direction
        textAlign: isRTL ? "right" : "left", // Added text alignment
      }}
    >
      <h5 
        className="mb-4 fw-semibold"
        style={{ textAlign: isRTL ? "right" : "left" }}
      >
        {t("change_password")} {/* New translation key */}
      </h5>

      {/* Current Password */}
      <div className="mb-3 position-relative">
        <label 
          className="form-label" 
          style={{ 
            color: "#909090", 
            fontSize: "14px",
            textAlign: isRTL ? "right" : "left"
          }}
        >
          {t("current_password")} {/* New translation key */}
        </label>
        <input
          type={showCurrent ? "text" : "password"}
          name="current_password"
          className="form-control rounded-2"
          style={{ 
            backgroundColor: "#f7f7f7", 
            borderColor: "#e0e0e0",
            paddingLeft: isRTL ? "40px" : "12px",
            paddingRight: isRTL ? "12px" : "40px",
            textAlign: isRTL ? "right" : "left"
          }}
          required
          dir="ltr" // Force LTR for password fields
        />
        <span
          onClick={() => setShowCurrent(!showCurrent)}
          style={{
            position: "absolute",
            right: isRTL ? "auto" : "10px",
            left: isRTL ? "10px" : "auto",
            top: "38px",
            cursor: "pointer",
            color: "#666",
          }}
        >
          {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
        </span>
      </div>

      {/* New Password */}
      <div className="mb-3 position-relative">
        <label 
          className="form-label" 
          style={{ 
            color: "#909090", 
            fontSize: "14px",
            textAlign: isRTL ? "right" : "left"
          }}
        >
          {t("new_password")} {/* New translation key */}
        </label>
        <input
          type={showNew ? "text" : "password"}
          name="new_password"
          className="form-control rounded-2"
          style={{ 
            backgroundColor: "#f7f7f7", 
            borderColor: "#e0e0e0",
            paddingLeft: isRTL ? "40px" : "12px",
            paddingRight: isRTL ? "12px" : "40px",
            textAlign: isRTL ? "right" : "left"
          }}
          required
          dir="ltr" // Force LTR for password fields
        />
        <span
          onClick={() => setShowNew(!showNew)}
          style={{
            position: "absolute",
            right: isRTL ? "auto" : "10px",
            left: isRTL ? "10px" : "auto",
            top: "38px",
            cursor: "pointer",
            color: "#666",
          }}
        >
          {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
        </span>
      </div>

      {/* Confirm New Password */}
      <div className="mb-4 position-relative">
        <label 
          className="form-label" 
          style={{ 
            color: "#909090", 
            fontSize: "14px",
            textAlign: isRTL ? "right" : "left"
          }}
        >
          {t("confirm_password")} {/* New translation key */}
        </label>
        <input
          type={showConfirm ? "text" : "password"}
          name="confirm_password"
          className="form-control rounded-2"
          style={{ 
            backgroundColor: "#f7f7f7", 
            borderColor: "#e0e0e0",
            paddingLeft: isRTL ? "40px" : "12px",
            paddingRight: isRTL ? "12px" : "40px",
            textAlign: isRTL ? "right" : "left"
          }}
          required
          dir="ltr" // Force LTR for password fields
        />
        <span
          onClick={() => setShowConfirm(!showConfirm)}
          style={{
            position: "absolute",
            right: isRTL ? "auto" : "10px",
            left: isRTL ? "10px" : "auto",
            top: "38px",
            cursor: "pointer",
            color: "#666",
          }}
        >
          {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
        </span>
      </div>

      <button
        type="submit"
        className="btn"
        disabled={loading}
        style={{
          backgroundColor: "#e15d4f",
          color: "#ffffff",
          padding: "8px 20px",
          borderRadius: "6px",
          fontWeight: "500",
          fontSize: "14px",
          marginRight: isRTL ? "auto" : "0",
          marginLeft: isRTL ? "0" : "auto",
          display: "block"
        }}
      >
        {loading ? t("changing_password") : t("change_password")} {/* New translation keys */}
      </button>
    </form>
  );
};

export default Password;
